// src/runtime/Autopilot/types.ts
// ============================================
// Company Autopilot - 类型定义（Wave 3：公司自动驾驶）
//
// 职责：
//   1. 定义自动驾驶模式 / 自主决策 / 资源分配 / 任务重平衡 / 机会排序完整类型体系
//   2. 提供 AutopilotDailyReport 统一输出结构（每日运行汇总）
//   3. 提供查询/命令枚举（AutopilotQuery / AutopilotCommand）
//
// 设计原则：
//   - Autopilot 是公司自动驾驶层（Hermes 自主决定今天干什么）
//   - 与 EnterpriseBrain / GrowthEngine 区分：
//       * Brain 是企业决策编排层（目标 / 战略 / KPI / 风险）
//       * Growth 是增长执行层（A/B 测试 / 爆款 / 预算 / 协同 / 实验）
//       * Autopilot 是自动驾驶层（综合 Brain + Growth + CRM 数据，自主决策今日运营策略）
//   - 失败非阻塞；LLM 不可用降级到规则化
//   - 默认模式：Assisted（Hermes 建议，人工批准）
// ============================================

// ============================================
// 自动驾驶模式
// ============================================

/**
 * 自动驾驶模式
 *
 * - Manual    : 手动模式，所有决策需人工批准
 * - Assisted  : 辅助模式，Hermes 建议，人工批准（默认）
 * - Automatic : 自动模式，低风险自动执行，高风险需批准
 * - FullAuto  : 全自动模式，所有决策自动执行
 */
export enum AutopilotMode {
  Manual = 'manual',
  Assisted = 'assisted',
  Automatic = 'automatic',
  FullAuto = 'full_auto',
}

// ============================================
// 自主决策
// ============================================

/**
 * 自主决策定义
 *
 * 决策类型：
 *   - product_focus      : 今天重点推广哪个产品
 *   - content_strategy   : 内容策略调整
 *   - channel_investment : 哪个平台需要加大内容投入
 *   - customer_priority  : 哪个客户必须优先跟进
 *   - project_pause      : 哪个项目应该暂停资源投入
 *   - workflow_rearrange : 哪些工作流需要重排
 *   - revenue_pursuit    : 今天最值得赚的那笔钱是什么
 */
export interface AutonomousDecision {
  /** 决策 ID */
  id: string;
  /** 日期（YYYY-MM-DD） */
  date: string;
  /** 决策类型 */
  type:
    | 'product_focus'
    | 'content_strategy'
    | 'channel_investment'
    | 'customer_priority'
    | 'project_pause'
    | 'workflow_rearrange'
    | 'revenue_pursuit';
  /** 标题 */
  title: string;
  /** 决策理由（基于哪些数据） */
  rationale: string;
  /** 预期影响 */
  expectedImpact: {
    /** 预期收入影响 */
    revenue?: number;
    /** 预期受众影响 */
    audience?: number;
    /** 预期效率提升 */
    efficiency?: number;
    /** 风险等级 */
    risk?: 'low' | 'medium' | 'high';
  };
  /** 置信度 0-1 */
  confidence: number;
  /** 是否已批准 */
  approved: boolean;
  /** 是否自动执行 */
  autoExecuted: boolean;
  /** 执行时间（ISO） */
  executedAt?: string;
  /** 执行结果 */
  outcome?: 'success' | 'failure' | 'pending';
  /** 关联数据来源与指标 */
  relatedData: {
    /** 数据来源（enterprise_brain / growth_engine / crm / revenue） */
    source: string;
    /** 关键指标 */
    metrics: Record<string, number>;
  };
}

// ============================================
// 资源分配
// ============================================

/**
 * 资源分配建议
 */
export interface ResourceAllocation {
  /** 产品名称 */
  product: string;
  /** 当前分配（0-1） */
  currentAllocation: number;
  /** 建议分配（0-1） */
  recommendedAllocation: number;
  /** 调整理由 */
  reason: string;
  /** 预期 ROI */
  expectedROI: number;
}

// ============================================
// 任务重平衡
// ============================================

/**
 * 任务重平衡建议
 */
export interface TaskRebalance {
  /** 任务 ID */
  taskId: string;
  /** 原优先级 */
  originalPriority: number;
  /** 新优先级 */
  newPriority: number;
  /** 调整理由 */
  reason: string;
  /** 动作类型 */
  action: 'promote' | 'demote' | 'postpone' | 'cancel' | 'expedite';
}

// ============================================
// 机会排序
// ============================================

/**
 * 已排序的机会
 */
export interface PrioritizedOpportunity {
  /** 机会 ID */
  id: string;
  /** 标题 */
  title: string;
  /** 综合得分 0-100 */
  score: number;
  /** 潜在收入 */
  potentialRevenue: number;
  /** 成功概率 0-1 */
  probability: number;
  /** 所需投入 */
  effort: 'low' | 'medium' | 'high';
  /** 时间范围 */
  timeframe: string;
  /** 推荐意见 */
  recommendation: string;
}

// ============================================
// 自动驾驶日报
// ============================================

/**
 * 今日计划
 */
export interface AutopilotTodayPlan {
  /** 今天主要做什么 */
  primaryFocus: string;
  /** 次要任务 */
  secondaryTasks: string[];
  /** 暂停的项目 */
  pausedItems: string[];
  /** 今天的目标收入 */
  revenueTarget: number;
}

/**
 * 自动驾驶性能指标
 */
export interface AutopilotPerformance {
  /** 历史决策准确率 0-1 */
  decisionAccuracy: number;
  /** 自动执行率 0-1 */
  autoExecutionRate: number;
  /** 平均置信度 0-1 */
  averageConfidence: number;
}

/**
 * 自动驾驶每日报告
 */
export interface AutopilotDailyReport {
  /** 日期（YYYY-MM-DD） */
  date: string;
  /** 当前模式 */
  mode: AutopilotMode;
  /** 决策列表 */
  decisions: AutonomousDecision[];
  /** 资源分配建议 */
  resourceAllocations: ResourceAllocation[];
  /** 任务重平衡建议 */
  taskRebalances: TaskRebalance[];
  /** 已排序的机会列表 */
  opportunities: PrioritizedOpportunity[];
  /** 今日计划 */
  todayPlan: AutopilotTodayPlan;
  /** 自动执行的决策数量 */
  autoExecutedCount: number;
  /** 待批准的决策数量 */
  pendingApprovalCount: number;
  /** 性能指标 */
  performance: AutopilotPerformance;
  /** Vault 报告路径（写入成功时返回） */
  reportPath?: string;
  /** 是否使用了 LLM */
  usedLLM: boolean;
  /** 一句话总结 */
  summary: string;
}

// ============================================
// 查询/命令枚举
// ============================================

/**
 * Autopilot 查询类型（只读）
 */
export enum AutopilotQuery {
  /** 获取指定日期的自动驾驶报告 */
  GetReport = 'get_report',
  /** 获取最新自动驾驶报告 */
  GetLatestReport = 'get_latest_report',
  /** 获取自动驾驶状态 */
  GetStatus = 'get_status',
  /** 获取今日计划 */
  GetTodayPlan = 'get_today_plan',
  /** 获取决策列表 */
  GetDecisions = 'get_decisions',
  /** 获取待批准决策 */
  GetPending = 'get_pending',
  /** 获取资源分配建议 */
  GetResourceAllocation = 'get_resource_allocation',
  /** 获取机会列表 */
  GetOpportunities = 'get_opportunities',
  /** 获取当前模式 */
  GetMode = 'get_mode',
}

/**
 * Autopilot 命令类型（写操作 / 触发）
 */
export enum AutopilotCommand {
  /** 运行自动驾驶（核心入口） */
  Run = 'run',
  /** 设置模式 */
  SetMode = 'set_mode',
  /** 批准决策 */
  ApproveDecision = 'approve_decision',
  /** 驳回决策 */
  RejectDecision = 'reject_decision',
}

// ============================================
// 统一返回类型
// ============================================

/**
 * Autopilot 统一返回类型
 */
export interface AutopilotResult<T = unknown> {
  /** 是否成功 */
  success: boolean;
  /** 返回数据 */
  data?: T;
  /** 错误信息 */
  error?: string;
  /** 时间戳（ISO） */
  timestamp: string;
}

// ============================================
// 默认导出（便于 IDE 自动补全）
// ============================================

export default {
  AutopilotMode,
  AutopilotQuery,
  AutopilotCommand,
};
